/* Aurmina - shared scroll reveal
   REVISION b
   Revision:      b - 26 Aug 2026
   Goes in:       assets/aurmina-reveal.js
   Pairs with:    every section that emits a [data-aurmina-reveal] attribute

   REV B FIXES THE HALF OF THE REVEAL CONTRACT THAT WAS NEVER WRITTEN.

   Every stylesheet in this project that was "correctly gated" during the
   defect sweep starts its reveal state like this:

       html.aurmina-reveal-on .aurmina-x [data-aurmina-reveal] { opacity: 0 }

   The class in that selector was recorded in the registry as something this
   script sets as its first statement. Rev a never set it. The consequence is
   not a broken page - it is the fail-safe direction, so content renders
   visible and unanimated - but it means the reveal has been inert on every
   gated section since the sweep began. Sections that were NOT gated animate
   normally, which is why the behaviour looked inconsistent rather than
   missing.

   Two changes, both one-liners:

     1. The gate class is now added to the document element as the first
        statement, before anything else runs. Because the script tag carries
        no defer, this happens before the browser paints the sections below
        it, so there is no flash-then-hide.
     2. A window-level guard, also recorded in the registry and also absent.
        Seven sections on one page each emit their own script tag; browsers
        execute every one of them. Without the guard that is seven
        IntersectionObservers over the same nodes.

   Vanilla, no dependencies. Respects prefers-reduced-motion.
   Re-binds on shopify:section:load so the theme editor behaves.

   EXPECT VISIBLE CHANGE ON UPLOAD. Every gated section across the built
   pages begins animating on scroll. That is the owner's 25 Aug decision
   landing, not a regression. Any section that should stay static is turned
   off with its own "Fade content in on scroll" checkbox.
   ========================================================================== */
(function () {
  'use strict';

  /* Guard first: the script tag is emitted per section, so this file is
     executed once per section on the page. */
  if (window.__aurminaReveal) return;
  window.__aurminaReveal = true;

  /* The gate every section stylesheet waits for. Set before paint so the
     start state applies to markup that has not rendered yet. */
  document.documentElement.classList.add('aurmina-reveal-on');

  var REDUCED =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function showAll(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.add('is-revealed');
    }
  }

  function bind(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-aurmina-reveal]:not(.is-revealed)');
    if (!nodes.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      showAll(nodes);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: '0px 0px -40px 0px' }
    );

    for (var i = 0; i < nodes.length; i++) {
      observer.observe(nodes[i]);
    }
  }

  /* Count-up for numeric stats. Opt-in via data-aurmina-count="245". */
  function bindCounters(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-aurmina-count]:not(.is-counted)');

    for (var i = 0; i < nodes.length; i++) {
      (function (el) {
        var target = parseFloat(el.getAttribute('data-aurmina-count'));
        if (isNaN(target)) return;

        el.classList.add('is-counted');

        if (REDUCED || !('IntersectionObserver' in window)) {
          el.textContent = target;
          return;
        }

        var run = function () {
          var start = null;
          var step = function (ts) {
            if (!start) start = ts;
            var p = Math.min((ts - start) / 1400, 1);
            var eased = p * p * (3 - 2 * p);
            el.textContent = Math.floor(eased * target);
            if (p < 1) {
              window.requestAnimationFrame(step);
            } else {
              el.textContent = target;
            }
          };
          window.requestAnimationFrame(step);
        };

        var io = new IntersectionObserver(
          function (entries) {
            if (entries[0].isIntersecting) {
              run();
              io.disconnect();
            }
          },
          { threshold: 0.5 }
        );
        io.observe(el);
      })(nodes[i]);
    }
  }

  function init(root) {
    bind(root);
    bindCounters(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
